import {
  MINDSET_BUSINESS_IDENTITY_PRICE,
  MINDSET_BUSINESS_WORKSPACE_PRICE,
  MINDSET_INDIVIDUAL_PRICE,
} from "./businessPricing";

export type PreviewModeId = "message" | "voice" | "video" | "groups";

export interface PreviewMode {
  id: PreviewModeId;
  label: string;
  title: string;
  body: string;
  status: string;
  primaryLine: string;
  secondaryLine: string;
  metrics: readonly string[];
  rosterPreview?: readonly RosterPreview[];
}

export interface RosterPreview {
  id: string;
  title: string;
  sender: string;
  body: string;
  unreadCount: number;
  timestamp: string;
  state: string;
}

export interface Surface {
  id: string;
  title: string;
  body: string;
  details: readonly string[];
}

export interface PlatformStep {
  id: string;
  title: string;
  body: string;
  proof: string;
}

export interface CommercialPlan {
  id: string;
  title: string;
  audience: string;
  price: string;
  body: string;
  includes: readonly string[];
  boundary: string;
}

export interface TrustItem {
  id: string;
  title: string;
  body: string;
}

export interface HelpTopic {
  id: string;
  title: string;
  body: string;
  href: string;
}

export interface HelpArticle {
  id: string;
  title: string;
  body: string;
  href: string;
}

export interface DeveloperEntry {
  id: string;
  title: string;
  body: string;
  href: string;
  label: string;
}

export interface DeveloperUseCase {
  id: string;
  title: string;
  body: string;
}

export interface LoginProviderConfig {
  providerName: string;
  signInLabel: string;
  getStartedLabel: string;
  status: string;
  routing: string;
  supportCopy: string;
  baseUrl: string;
  redirectPath: string;
}

export interface HomeStory {
  id: string;
  eyebrow: string;
  title: string;
  body: string;
  points: readonly string[];
}

export interface FeatureHighlight {
  id: string;
  title: string;
  body: string;
  points: readonly string[];
}

export interface HomeDestination {
  id: string;
  title: string;
  body: string;
  href: string;
  label: string;
}

export interface LegalPageSection {
  title: string;
  body: string;
  points: readonly string[];
}

export type DashboardPreviewId = "user" | "tenant" | "superadmin";

export interface DashboardMetric {
  label: string;
  value: string;
  detail: string;
}

export interface DashboardLane {
  title: string;
  state: string;
  detail: string;
}

export interface DashboardPreview {
  id: DashboardPreviewId;
  label: string;
  eyebrow: string;
  title: string;
  summary: string;
  primaryAction: string;
  secondaryAction: string;
  statusLabel: string;
  statusValue: string;
  metrics: readonly DashboardMetric[];
  lanes: readonly DashboardLane[];
  activity: readonly string[];
}

export interface SuperadminModule {
  id: string;
  title: string;
  state: string;
  body: string;
  checks: readonly string[];
}

export const developerPortalConfig = {
  defaultBaseUrl: "http://localhost:4183",
  environmentVariable: "VITE_MINDSET_DEVELOPER_PORTAL_URL",
} as const;

export const loginProviderConfig: LoginProviderConfig = {
  providerName: loginProviderName(),
  signInLabel: "Sign in with VB Toni",
  getStartedLabel: "Get Started with Verified By Toni",
  status: "Verified identity access",
  routing:
    "Mindset uses the Verified By Toni registration and login process for identity-first access.",
  supportCopy:
    "Mindset does not collect identity documents on this page. Toni handles verification and returns the approved user to Mindset.",
  baseUrl: loginProviderBaseUrl(),
  redirectPath: "/auth/callback",
};

function loginProviderName() {
  const configuredProvider =
    import.meta.env.VITE_MINDSET_LOGIN_PROVIDER_NAME?.trim();

  return configuredProvider || "VB Toni";
}

function loginProviderBaseUrl() {
  const configuredBaseUrl = import.meta.env.VITE_VB_TONI_BASE_URL?.trim();

  return (configuredBaseUrl || "https://vbtoni.com").replace(/\/+$/, "");
}

export function mindsetToniRedirectUri() {
  const configuredRedirect =
    import.meta.env.VITE_MINDSET_TONI_REDIRECT_URI?.trim();

  if (configuredRedirect) {
    return configuredRedirect;
  }

  if (typeof window !== "undefined" && window.location?.origin) {
    return `${window.location.origin}${loginProviderConfig.redirectPath}`;
  }

  return `http://localhost:4184${loginProviderConfig.redirectPath}`;
}

export function toniAccessHref(mode: "signin" | "register") {
  const url = new URL(`${loginProviderConfig.baseUrl}/signin`);
  url.searchParams.set("redirect", mindsetToniRedirectUri());

  if (mode === "register") {
    url.searchParams.set("mode", "register");
  }

  return url.toString();
}

function developerPortalHref(sectionId: "api" | "sdks" | "onboarding") {
  return `${developerPortalBaseUrl()}/#${sectionId}`;
}

function developerPortalBaseUrl() {
  const configuredBaseUrl =
    import.meta.env.VITE_MINDSET_DEVELOPER_PORTAL_URL?.trim();

  return (
    configuredBaseUrl || developerPortalConfig.defaultBaseUrl
  ).replace(/\/+$/, "");
}

export const previewModes: Record<PreviewModeId, PreviewMode> = {
  message: {
    id: "message",
    label: "Text",
    title: "Your chats, at a glance",
    body:
      "One-to-one chats and groups with photos, reactions, and read receipts. Notifications never show more than you allow.",
    status: "Maya is typing",
    primaryLine: "Can you talk for two minutes?",
    secondaryLine: "Delivered just now",
    metrics: ["Read receipts", "Reactions", "Private previews"],
    rosterPreview: [
      {
        id: "maya",
        title: "Maya Lane",
        sender: "Maya",
        body: "Can you talk for two minutes?",
        unreadCount: 2,
        timestamp: "Now",
        state: "Typing",
      },
      {
        id: "andre",
        title: "Andre Okafor",
        sender: "Andre",
        body: "Sending the photos now.",
        unreadCount: 1,
        timestamp: "4m",
        state: "Delivered",
      },
      {
        id: "family",
        title: "Family room",
        sender: "Priya",
        body: "Video is ready whenever you are.",
        unreadCount: 0,
        timestamp: "12m",
        state: "Read",
      },
    ],
  },
  voice: {
    id: "voice",
    label: "Voice",
    title: "A free call, in progress",
    body:
      "Individual voice calls between Mindset users are free. They ring like a real call, and you can answer from your lock screen.",
    status: "On a call",
    primaryLine: "Mindset voice call",
    secondaryLine: "Free between Mindset users",
    metrics: ["Free calls", "Rings like a real call", "Answer from your lock screen"],
  },
  video: {
    id: "video",
    label: "Video",
    title: "Ask first, then camera",
    body:
      "Turn any call into a video call in one tap. The other person's camera only turns on after they say yes.",
    status: "Video request",
    primaryLine: "Switch to video?",
    secondaryLine: "They can accept or decline before the camera starts",
    metrics: ["One tap to video", "Ask before camera", "Free between Mindset users"],
  },
  groups: {
    id: "groups",
    label: "Groups",
    title: "The family room, right now",
    body:
      "Simple group conversations. See who's there, who's typing, and catch up when you're ready.",
    status: "3 people here",
    primaryLine: "Priya: Dinner at ours on Sunday?",
    secondaryLine: "Two people are typing",
    metrics: ["Group chats", "See who's typing", "Mute anytime"],
    rosterPreview: [
      {
        id: "family",
        title: "Family room",
        sender: "Priya",
        body: "Dinner at ours on Sunday?",
        unreadCount: 3,
        timestamp: "Now",
        state: "Typing",
      },
      {
        id: "trip",
        title: "Trip planning",
        sender: "Maya",
        body: "Flights are booked!",
        unreadCount: 1,
        timestamp: "9m",
        state: "Delivered",
      },
      {
        id: "book",
        title: "Book club",
        sender: "Andre",
        body: "Chapter twelve tonight.",
        unreadCount: 0,
        timestamp: "1h",
        state: "Read",
      },
    ],
  },
};

export const homeStories: readonly HomeStory[] = [
  {
    id: "messages",
    eyebrow: "chat & groups",
    title: "Encrypted Messaging",
    body:
      "Encrypted 1-on-1 and group chats with media, reactions, and private previews — without giving out your phone number.",
    points: [],
  },
  {
    id: "calls",
    eyebrow: "voice & video",
    title: "Free HD Calls & Video",
    body:
      "Encrypted voice and video calls between Mindset handles. Rings on your lock screen just like a regular call.",
    points: [],
  },
  {
    id: "privacy",
    eyebrow: "handle identity",
    title: "Handle Privacy",
    body:
      "Communicate securely using your @handle. Built-in encryption, receipt controls, and instant account deletion.",
    points: [],
  },
];

export const featureHighlights: readonly FeatureHighlight[] = [
  {
    id: "messages",
    title: "Encrypted Messaging",
    body:
      "1-on-1 and group chats with media sharing, instant reactions, and private notification previews.",
    points: [],
  },
  {
    id: "calls",
    title: "Free HD Voice & Video",
    body:
      "Crystal-clear encrypted voice calls that ring on your lock screen, with seamless 1-tap video upgrade.",
    points: [],
  },
  {
    id: "privacy",
    title: "Handle Privacy",
    body:
      "Communicate using your @handle. Your phone number is never collected, exposed, or required.",
    points: [],
  },
];

export const homeDestinations: readonly HomeDestination[] = [
  {
    id: "features",
    title: "Explore Features",
    body: "Private text, voice, video, and group chats.",
    href: "/features",
    label: "View all features",
  },
  {
    id: "apps",
    title: "Download Apps",
    body: "Available on iOS, Android, and Web browsers.",
    href: "/apps",
    label: "Get Mindset",
  },
  {
    id: "platform",
    title: "Developer Platform",
    body: "Embed messaging & calling into your product.",
    href: "/platform",
    label: "Explore SDKs",
  },
  {
    id: "support",
    title: "Help Center",
    body: "Account setup, handles, and privacy guides.",
    href: "/support",
    label: "Visit Help Center",
  },
];

export const dashboardPreviews: readonly DashboardPreview[] = [
  {
    id: "user",
    label: "User",
    eyebrow: "consumer command center",
    title: "User dashboard",
    summary:
      "A clean home for conversations, calls, contacts, safety, and account controls after a Mindset user signs in.",
    primaryAction: "Start a message",
    secondaryAction: "Review calls",
    statusLabel: "Account state",
    statusValue: "Mindset session ready",
    metrics: [
      {
        label: "Messages",
        value: "18",
        detail: "Direct and group threads with receipts and privacy-safe previews.",
      },
      {
        label: "Calls",
        value: "4",
        detail: "Audio, video, missed-call recovery, and device handoff.",
      },
      {
        label: "Safety",
        value: "On",
        detail: "Block list, reports, profile visibility, and device controls.",
      },
    ],
    lanes: [
      {
        title: "Priority room",
        state: "Maya is typing",
        detail: "Message composer, attachment tray, reactions, and call upgrade.",
      },
      {
        title: "Call history",
        state: "Missed call recovered",
        detail: "Return-call action, voicemail placeholder, and device route.",
      },
      {
        title: "Account controls",
        state: "Identity verified",
        detail: "Profile, notifications, privacy, deletion, and sign-out.",
      },
    ],
    activity: [
      "New message preview kept private",
      "Video upgrade requires consent",
      "Device token refresh queued after login",
    ],
  },
  {
    id: "tenant",
    label: "Tenant",
    eyebrow: "company workspace",
    title: "Tenant dashboard",
    summary:
      "Operators can track users, calls, messaging health, API usage, integrations, support, and readiness without touching protected production controls.",
    primaryAction: "Open users",
    secondaryAction: "View API health",
    statusLabel: "Tenant",
    statusValue: "mindset-lab",
    metrics: [
      {
        label: "Active users",
        value: "2,418",
        detail: "Seats, invites, roles, devices, and stale session cleanup.",
      },
      {
        label: "API health",
        value: "99.95%",
        detail: "Sessions, devices, calls, realtime tickets, and webhooks.",
      },
      {
        label: "Tickets",
        value: "6",
        detail: "Tenant support queue with sanitized evidence and ownership.",
      },
    ],
    lanes: [
      {
        title: "Integration suite",
        state: "SDK examples current",
        detail: "TypeScript, Android, iOS, webhook receiver, and smoke proof.",
      },
      {
        title: "Usage controls",
        state: "Read-only limits",
        detail: "Quotas, rate view, capability flags, and manifest review.",
      },
      {
        title: "Release evidence",
        state: "Awaiting device proof",
        detail: "Store screenshots, console review, physical device, and binder.",
      },
    ],
    activity: [
      "Webhook receiver manifest reviewed",
      "Support escalation assigned",
      "No paid capacity or production credential issued",
    ],
  },
  {
    id: "superadmin",
    label: "Platform operations",
    eyebrow: "protected operator cockpit",
    title: "Platform operations",
    summary:
      "Mindset owners get tenant control, evidence gates, incidents, support oversight, and release boundaries in one command view.",
    primaryAction: "Review tenants",
    secondaryAction: "Open evidence",
    statusLabel: "Global state",
    statusValue: "Approval gates locked",
    metrics: [
      {
        label: "Tenants",
        value: "2",
        detail: "mindset-lab plus TERMS, with TERMS compatibility protected.",
      },
      {
        label: "Readiness",
        value: "17/21",
        detail: "Local checklist state until store, device, and Apple evidence land.",
      },
      {
        label: "Risk",
        value: "Guarded",
        detail: "No publish, billing, hosted CI, or production mutation from UI.",
      },
    ],
    lanes: [
      {
        title: "Tenant approvals",
        state: "Manual review",
        detail: "Manifest proposals, capability flags, owners, and contracts.",
      },
      {
        title: "Audit logs",
        state: "Required",
        detail: "Role changes, evidence updates, release decisions, and support access.",
      },
      {
        title: "TERMS protection",
        state: "Compatibility contract",
        detail: "No endpoint, payload, tenant behavior, or native bridge breakage.",
      },
    ],
    activity: [
      "Play draft evidence captured locally",
      "iOS proof waits on approved Mac/App Store path",
      "TERMS compatibility remains launch-critical",
    ],
  },
];

export const surfaces: readonly Surface[] = [
  {
    id: "ios",
    title: "Mindset for iPhone",
    body:
      "Private messages and free calls that feel right at home on iOS.",
    details: [
      "Calls ring like real calls",
      "Notifications keep previews private",
      "Profile and privacy settings in one place",
    ],
  },
  {
    id: "android",
    title: "Mindset for Android",
    body:
      "The same private identity, messages, and free calls — built for Android.",
    details: [
      "Answer calls from your lock screen",
      "Notifications stay private",
      "Simple profile and device controls",
    ],
  },
  {
    id: "web",
    title: "Mindset on the web",
    body:
      "Manage your company team from a secure desktop workspace.",
    details: [
      "Team roster and company governance",
      "Nothing to install",
    ],
  },
];

export const platformSteps: readonly PlatformStep[] = [
  {
    id: "tenant",
    title: "Your own private workspace",
    body:
      "Your company's users, conversations, and calls live in a workspace that is completely separate from every other company's.",
    proof: "Company workspaces stay isolated.",
  },
  {
    id: "sdk",
    title: "SDKs for web, iOS, and Android",
    body:
      "TypeScript, Android, and iOS SDKs with working examples for messages, calls, video, and profiles.",
    proof: "Developers get examples they can follow.",
  },
  {
    id: "webhooks",
    title: "Events delivered to your systems",
    body:
      "Signed webhooks tell your systems the moment something happens, so your product can react in real time.",
    proof: "Every event can be verified by the receiver.",
  },
  {
    id: "release",
    title: "APIs that stay stable",
    body:
      "When you build on Mindset, we keep it working. The behavior your integration depends on does not change out from under you.",
    proof: "Stability is part of the product.",
  },
];

export const commercialPlans: readonly CommercialPlan[] = [
  {
    id: "consumer",
    title: "People",
    audience: "The free Mindset app",
    price: MINDSET_INDIVIDUAL_PRICE.displayPrice,
    body:
      "Message, call, and video chat with one verified profile — no phone number needed.",
    includes: [
      "Private one-to-one and group messaging",
      "Free individual voice and video calls",
      "Full control of your profile and privacy",
    ],
    boundary:
      "Individual voice and video calls between Mindset users are free. Calls use your internet connection, so carrier data charges may apply.",
  },
  {
    id: "tenant",
    title: "Companies",
    audience: "Business identity and team communications",
    price: `${MINDSET_BUSINESS_IDENTITY_PRICE.displayPrice} + ${MINDSET_BUSINESS_WORKSPACE_PRICE.displayPrice}`,
    body:
      "Reserve business @ identities and run team messaging, voice, and video without forcing employees into phone numbers.",
    includes: [
      "Verified business @ identities billed annually",
      "Team communications workspace at one flat annual rate",
      "Admin controls for members, calls, groups, and apps",
    ],
    boundary:
      "Each business-owned @ is billed yearly. The communications workspace stays one flat annual subscription.",
  },
  {
    id: "enterprise",
    title: "Enterprise",
    audience: "For larger organizations",
    price: "Custom",
    body:
      "Guided onboarding and deeper support for teams with more demanding needs.",
    includes: [
      "Guided onboarding with our team",
      "Priority support with clear ownership",
      "Data separation and retention reviews",
    ],
    boundary:
      "Talk to us about enterprise terms.",
  },
];

export const trustItems: readonly TrustItem[] = [
  {
    id: "terms",
    title: "No surprise changes",
    body:
      "If the platform needs to change, you hear it from us first — with notice and a clear path forward, never a breaking surprise.",
  },
  {
    id: "privacy",
    title: "Notification privacy",
    body:
      "Notifications are designed to respect message privacy and never expose more than people allow.",
  },
  {
    id: "cost",
    title: "Your data stays yours",
    body:
      "Your workspace is separate from every other company's. Your users and their conversations belong to you.",
  },
  {
    id: "support",
    title: "Support that answers",
    body:
      "Clear support paths for your team, from your first request to production questions.",
  },
];

export const developerEntries: readonly DeveloperEntry[] = [
  {
    id: "api-reference",
    title: "API reference",
    body:
      "Sign in to read the customer API reference and integration requirements.",
    href: developerPortalHref("api"),
    label: "Open API docs",
  },
  {
    id: "sdk-examples",
    title: "SDKs",
    body:
      "Installation guides for TypeScript, Android, and iOS. Authorized SDK downloads stay in your company workspace.",
    href: developerPortalHref("sdks"),
    label: "View SDKs",
  },
  {
    id: "tenant-onboarding",
    title: "Get onboarded",
    body:
      "Register an application, verify domains and native app ownership, configure SDK values, and review checkout and webhook boundaries.",
    href: developerPortalHref("onboarding"),
    label: "Start onboarding",
  },
];

export const developerUseCases: readonly DeveloperUseCase[] = [
  {
    id: "chat",
    title: "Chat inside your app",
    body:
      "Give your users real conversations without building messaging from scratch.",
  },
  {
    id: "calls",
    title: "Voice and video between your users",
    body:
      "Real-time calling, ready-made — no media infrastructure to run.",
  },
  {
    id: "support",
    title: "Support conversations",
    body:
      "Let customers reach your team through a private, verified channel.",
  },
  {
    id: "owned-product",
    title: "Connect your existing product",
    body:
      "Bring your web domain, native package IDs, identity issuer, webhooks, and billing contract through one neutral onboarding path.",
  },
];

export const superadminModules: readonly SuperadminModule[] = [
  {
    id: "tenants",
    title: "Tenant control",
    state: "Approval required",
    body:
      "Create, review, suspend, offboard, and inspect tenants without exposing secrets or mutating production from public pages.",
    checks: [
      "Tenant manifest status",
      "Capability flags and owner contacts",
      "TERMS compatibility lock",
    ],
  },
  {
    id: "identity",
    title: "Identity and roles",
    state: "Firebase identity",
    body:
      "Route users through Mindset sign-in, then separate consumer, tenant operator, support, and platform operator permissions.",
    checks: [
      "Provider configuration visible",
      "Role assignment audit trail",
      "Session and device review",
    ],
  },
  {
    id: "evidence",
    title: "Evidence gates",
    state: "17/21 local",
    body:
      "Track store screenshots, console review, physical-device acceptance, Apple proof, and final release binder status.",
    checks: [
      "Store screenshot evidence",
      "Physical device acceptance",
      "Final release binder",
    ],
  },
  {
    id: "releases",
    title: "Release controls",
    state: "Locked",
    body:
      "Separate build, upload, submit, publish, production activation, billing, and hosted-runner decisions.",
    checks: [
      "Exact source SHA binding",
      "Manual-only iOS proof",
      "No automatic GitHub CI triggers",
    ],
  },
  {
    id: "support",
    title: "Support oversight",
    state: "Split queues",
    body:
      "Keep consumer support, tenant support, incident intake, account deletion, and protected access review distinct.",
    checks: [
      "Sanitized evidence only",
      "Account deletion ownership",
      "Incident escalation path",
    ],
  },
  {
    id: "safety",
    title: "Safety and audit",
    state: "Mandatory",
    body:
      "Surface risky changes, approvals, support access, device events, release notes, and tenant-impacting decisions.",
    checks: [
      "Audit logs",
      "Notification privacy",
      "Secret-free operator views",
    ],
  },
];

export const helpTopics: readonly HelpTopic[] = [
  {
    id: "getting-started",
    title: "Get started",
    body:
      "Create your verified profile and sign in to Mindset.",
    href: "/access",
  },
  {
    id: "messages",
    title: "Messages",
    body:
      "Sending messages, groups, photos, receipts, and notifications.",
    href: "/features",
  },
  {
    id: "calls",
    title: "Voice and video calls",
    body:
      "Free individual calls, switching to video, and call controls.",
    href: "/features",
  },
  {
    id: "privacy",
    title: "Privacy and safety",
    body:
      "Your profile, notifications, blocking, and reporting.",
    href: "/privacy",
  },
  {
    id: "account",
    title: "Account and devices",
    body:
      "Signing in on new devices, managing sessions, and deleting your account.",
    href: "/account-deletion",
  },
  {
    id: "business",
    title: "For companies and developers",
    body:
      "Building on Mindset — docs, SDKs, and company workspaces.",
    href: "/developers",
  },
];

export const popularHelpArticles: readonly HelpArticle[] = [
  {
    id: "free-calls",
    title: "Are Mindset calls free?",
    body:
      "Individual voice and video calls between Mindset users are free. Calls use your internet connection, so carrier data charges may apply.",
    href: "/terms",
  },
  {
    id: "no-phone-number",
    title: "How does Mindset work without phone numbers?",
    body:
      "Your verified profile is your identity — people reach you by name, not by number.",
    href: "/features",
  },
  {
    id: "get-started",
    title: "How do I create or access my account?",
    body:
      "Choose Sign In if you already have a Verified By Toni identity, or Get Started to create one.",
    href: "/access",
  },
  {
    id: "delete-account",
    title: "How do I delete my account?",
    body:
      "You can delete your account at any time — the account deletion page explains each step.",
    href: "/account-deletion",
  },
  {
    id: "business",
    title: "How can a company use Mindset?",
    body:
      "Companies can build Mindset's messaging, calls, and video into their own products.",
    href: "/platform",
  },
];

export const privacyPageSections: readonly LegalPageSection[] = [
  {
    title: "1. Verified identity without phone numbers",
    body:
      "Mindset replaces traditional phone-number tracking with verified handle profiles (@handle) powered by Verified By Toni.",
    points: [
      "We never require, collect, or store your personal mobile phone number",
      "Sign-in and identity verification are managed securely via Verified By Toni",
      "Your handle remains yours across devices without exposing phone contact lists",
    ],
  },
  {
    title: "2. End-to-end encryption & call security",
    body:
      "All 1-on-1 chats, group rooms, voice calls, and video sessions are protected by end-to-end encryption.",
    points: [
      "Message contents, attachments, and call audio/video streams are encrypted on your device",
      "Mindset servers cannot decrypt, read, or listen to your private communications",
      "Push notifications display only minimal metadata configured in your settings",
    ],
  },
  {
    title: "3. Data minimization & zero ad tracking",
    body:
      "Mindset operates on a strict data-minimization framework built for user privacy.",
    points: [
      "We never sell your personal information or metadata to third parties",
      "We do not track your physical location or build advertising profiles",
      "No third-party ad networks or tracking scripts run inside the Mindset app",
    ],
  },
  {
    title: "4. Personal & enterprise workspace isolation",
    body:
      "Mindset strictly separates consumer personal accounts from corporate enterprise tenant environments.",
    points: [
      "Personal conversations remain 100% private and outside company workspaces",
      "Enterprise tenant data is compartmentalized within designated tenant boundaries",
      "Company admins have zero access to personal non-workspace communications",
    ],
  },
  {
    title: "5. Your rights & account deletion",
    body:
      "You hold full authority over your data, connected devices, and account lifecycle.",
    points: [
      "Review connected devices and revoke active sessions anytime from Settings",
      "Initiate immediate account deletion to remove your profile, sessions, and credentials",
      "Transient server relay queues are purged automatically upon message delivery",
    ],
  },
];

export const termsPageSections: readonly LegalPageSection[] = [
  {
    title: "Using Mindset",
    body:
      "Mindset is for private messages, voice calls, and video calls between people.",
    points: [
      "Be yourself — impersonating others is not allowed",
      "Follow the safety and acceptable-use rules",
      "We may limit access to protect people from abuse or fraud",
    ],
  },
  {
    title: "Free individual calls",
    body:
      "Individual Mindset voice and video calls are free. Calls use your internet connection, so carrier data, roaming, or internet charges from your provider may still apply.",
    points: [
      "Free calling covers individual Mindset-to-Mindset voice and video calls",
      "Company and developer plans have their own terms",
      "Nothing on this site signs you up for a paid plan",
    ],
  },
  {
    title: "No emergency calling",
    body:
      "Mindset is not a replacement for your phone's emergency calling.",
    points: [
      "Do not rely on Mindset for 911 or other emergency numbers",
      "In an emergency, use your local emergency services",
    ],
  },
  {
    title: "Companies building on Mindset",
    body:
      "Companies that build on Mindset agree to separate terms for their workspace and integration.",
    points: [
      "Company workspaces are set up with our team",
      "Company use of the platform is covered by its own agreement",
    ],
  },
];
